import { Dialog, type SxProps, styled } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { BoxGradientBorder } from "~/app/components/BoxGradientBorder/BoxGradientBorder";
import { CTAButton } from "~/app/components/CTAButton/CTAButton";
import { SilverText } from "~/app/components/GradientTypography/SilverText";
import type { PurchaseItem } from "~/app/components/HOC/withPurchase";
import { useCommodityBalance } from "~/app/stores/commodities";
import { formatNumber } from "~/app/utils/format";
import iconHelpingHand from "~/assets/commodities/helping_hand.webp";
import iconHints from "~/assets/commodities/hints.webp";
import iconMultiplierFreeToken from "~/assets/commodities/multiplier_token.webp";
import iconSpinUpgradeToken from "~/assets/commodities/spin_token.webp";
import iconVowelDetector from "~/assets/commodities/vowel_blast.webp";
import imgShinyLayer from "~/assets/rays.svg";
import WofLogo from "~/assets/wof_logo.webp";
import { fireConffeti } from "./Confetti";

const commodityIconMap: Record<string, string> = {
  SpinUpgradeToken: iconSpinUpgradeToken,
  MultiplierFreeToken: iconMultiplierFreeToken,
  HelpingHand: iconHelpingHand,
  VowelDetector: iconVowelDetector,
  Hints: iconHints,
};

const StyledDialog = styled(Dialog)(() => ({
  "& .MuiPaper-root": {
    overflow: "visible",
    borderRadius: 0,
    margin: 2,
    background: "initial",
  },
}));

interface ModalPurchaseSuccessfulProps {
  open: boolean;
  onClose: () => void;
  purchaseItem: PurchaseItem | null;
}

export const ModalPurchaseSuccessful = ({
  open,
  onClose,
  purchaseItem,
}: ModalPurchaseSuccessfulProps) => {
  const [refModalState, setRefModalState] = useState<HTMLDivElement | null>(null);
  const diamondsBalance = useCommodityBalance("Diamonds");
  const refModal = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open && refModalState) {
      fireConffeti(refModal);
    }
  }, [open, refModalState]);

  const isPiggybank = !!purchaseItem?.piggyExtras;
  const purchaseItemIcon = purchaseItem?.icon || "";

  const DIALOG_TEXT_STYLES: SxProps = {
    fontFamily: "Lato",
    color: "#300046",
    fontSize: "20px",
    lineHeight: "28px",
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      ref={(ref) => {
        refModal.current = ref;
        setRefModalState(ref);
      }}
    >
      <BoxGradientBorder
        borderSize="2px"
        borderRadius="20px"
        gradient="linear-gradient(200deg,rgba(255, 255, 255, 1) 0%, rgba(202, 117, 241, 1) 25%, rgba(236, 213, 246, 1) 75%, rgba(125, 23, 172, 1) 100%)"
        fallbackColor="rgba(236, 213, 246, 1)"
      >
        <Box
          sx={{
            pt: "90px",
            px: 3.5,
            pb: 4,
            position: "relative",
            textAlign: "center",
            maxWidth: "436px",
            backgroundColor: "#FDFBFF",
            backgroundImage: "linear-gradient(180deg, #FDFBFF 0%, #CFB1FA 100%)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translate(-50%,-50%)",
              p: isPiggybank ? 0 : 1.25,
              width: "150px",
              height: "150px",
              aspectRatio: 1,
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#530C74",
              backgroundImage:
                "radial-gradient(98.22% 50.15% at 49.85% 50.31%, #AB19EE 0%, #530C74 61%, #300046 100%)",
              "& > img": {
                maxHeight: "100%",
              },
              overflow: "hidden",
            }}
          >
            <Box sx={{ position: "absolute", zIndex: -1, width: "200%" }}>
              <img src={imgShinyLayer} alt="" />
            </Box>
            <img src={purchaseItemIcon || WofLogo} alt="" />
          </Box>

          <Typography
            fontFamily="Lato"
            fontWeight={900}
            lineHeight={1}
            sx={{ fontSize: "26px", color: "#382053", letterSpacing: "-1px", mb: 2 }}
          >
            Congratulations!
          </Typography>

          <Typography sx={DIALOG_TEXT_STYLES}>Thank you for your purchase.</Typography>
          {purchaseItem?.includesDiamonds ? (
            <Typography sx={{ ...DIALOG_TEXT_STYLES, mb: 4 }}>
              Your new balance of <b>{formatNumber(diamondsBalance)}</b> diamonds is already
              available in-game
            </Typography>
          ) : (
            <Typography sx={{ ...DIALOG_TEXT_STYLES, mb: 4 }}>
              Your <b>{purchaseItem?.commodityDescription || "items"}</b>{" "}
              {purchaseItem?.isSingleCommodity ? "is" : "are"} already available in-game
            </Typography>
          )}

          {isPiggybank ? (
            <>
              <Typography sx={{ ...DIALOG_TEXT_STYLES, fontWeight: 900, mb: 2 }}>
                You also won these extra web rewards:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "74px",
                  mb: 2,
                }}
              >
                {purchaseItem?.piggyExtras?.map((extra, index, arr) => {
                  return (
                    <Box
                      key={`piggy-reward-${extra.commodity.toLowerCase()}`}
                      sx={{
                        width: extra.commodity === "Hints" ? "58px" : "74px",
                        position: "relative",
                        transform: "rotate(12deg)",
                        ml: -index * 1.75,
                        zIndex: arr.length - index,
                      }}
                    >
                      <img src={commodityIconMap[extra.commodity]} alt="" />
                    </Box>
                  );
                })}
              </Box>
            </>
          ) : (
            ""
          )}

          <CTAButton onClick={onClose} sx={{ px: 4.5, py: 1.5 }}>
            <SilverText
              fontFamily="Lato"
              fontWeight={900}
              sx={{
                fontSize: "24px",
                lineHeight: 1,
                filter: "drop-shadow(0px 3px 1px rgba(12, 12, 13, 0.60))",
              }}
            >
              Close
            </SilverText>
          </CTAButton>
        </Box>
      </BoxGradientBorder>
    </StyledDialog>
  );
};
